import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export interface UsersViewsProps {
  search: string;
  activeOnly: boolean;
  onSearchChange: (search: string) => void;
  onActiveOnlyChange: (activeOnly: boolean) => void;
}

// No org selects here on purpose — the organization tree owns org scoping.
export const UsersViews: React.FC<UsersViewsProps> = (props) => {
  const [term, setTerm] = useState(props.search);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastEmitted = useRef(props.search);

  useEffect(() => () => clearTimeout(debounce.current), []);

  // Sync external search changes into the input, but ignore the parent echoing
  // back a value this input just emitted so in-progress typing is never clobbered.
  useEffect(() => {
    if (props.search !== lastEmitted.current) {
      clearTimeout(debounce.current);
      setTerm(props.search);
      lastEmitted.current = props.search;
    }
  }, [props.search]);

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-y-2 pt-2">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          role="switch"
          aria-checked={props.activeOnly}
          onClick={() => props.onActiveOnlyChange(!props.activeOnly)}
          className="flex cursor-pointer items-center gap-2"
        >
          <span aria-hidden className={cn('h-3 w-3 border border-ink', props.activeOnly && 'bg-ink')} />
          <span className="text-scale-1 text-ink">Active only</span>
        </button>
      </div>
      <input
        value={term}
        placeholder="Search users…"
        onChange={(event) => {
          const { value } = event.target;
          setTerm(value);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => {
            lastEmitted.current = value;
            props.onSearchChange(value);
          }, 300);
        }}
        className="w-44 border-b border-rule-faint bg-transparent text-scale-1 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
        aria-label="Search users"
      />
    </div>
  );
};
